/**
 * Overview tab's "Day-of-Week Pattern" chart — raw absence-day counts by
 * weekday (Monday-Friday only; the source data is already weekend-excluded
 * upstream). Deliberately NOT a rate/percentage: that needs a working-days-
 * per-weekday denominator that doesn't exist anywhere in the warehouse yet.
 *
 * Reuses the same peak-highlight styling as MonthlyVolumeChart for the
 * current period, and applies the same treatment independently to the prior
 * period when "Compare last year" is on — last year's own busiest day is
 * highlighted in lightBlue, everything else muted grey, so the two years'
 * peaks (which can fall on different weekdays) are both visible at a glance
 * — grouped as a second bar stack alongside the current one rather than a
 * dashed line, since this is a bar chart.
 */
import { useState } from 'react';
import { Bar } from 'react-chartjs-2';
import type { AbsenceDayOfWeekPoint, AbsenceType } from '../services/absenceApi';
import { SICK_LEAVE_COLOR, CHILD_CARE_COLOR, pickDays, TogglePill, EmptyChartCard } from './AbsenceInsightsCharts';
import { InfoTooltip } from './InfoTooltip';

const MUTED_DARK = '#D7D7D7'; // pewter
const MUTED_LIGHT = '#F3F3F3'; // cotton

export function DayOfWeekChart({
  current,
  prior,
  absenceType,
}: {
  current: AbsenceDayOfWeekPoint[];
  prior: AbsenceDayOfWeekPoint[];
  absenceType: AbsenceType;
}) {
  const [showPrior, setShowPrior] = useState(false);

  if (current.length === 0) {
    return <EmptyChartCard title="Day-of-Week Pattern" message="No absence data in the selected window." />;
  }

  // The whole weekday breakdown is suppressed or safe together (it's not
  // per-day — see suppressDayOfWeek in the proxy), so a fully-suppressed
  // period is shown as an explicit message rather than a chart full of
  // invisible zero-height bars.
  if (current.every((p) => p.suppressed)) {
    return (
      <EmptyChartCard
        title="Day-of-Week Pattern"
        message="Hidden for privacy reasons — fewer than 5 employees match the current filters."
      />
    );
  }

  const showSl = absenceType !== 'csl';
  const showCsl = absenceType !== 'sl';

  // Peak day(s) — same tolerant-of-ties approach as MonthlyVolumeChart,
  // based on whichever metric the Absence Type filter currently shows.
  const totals = current.map((p) => pickDays(p, absenceType) ?? 0);
  const maxTotal = Math.max(...totals, 0);
  const isPeak = totals.map((t) => t >= maxTotal - 0.01 && maxTotal > 0);

  const priorByDay = new Map(prior.map((p) => [p.dayName, p]));

  // Prior-year peak — computed off last year's own data (independent of the
  // current year's peak, which can land on a different weekday), so toggling
  // "Compare last year" shows which day was highest that year too.
  const priorTotal = (dayName: string) => {
    const p = priorByDay.get(dayName);
    return p ? pickDays(p, absenceType) ?? 0 : 0;
  };
  const maxPriorTotal = Math.max(...prior.map((p) => pickDays(p, absenceType) ?? 0), 0);
  const isPriorPeak = current.map((p) => priorByDay.has(p.dayName) && priorTotal(p.dayName) >= maxPriorTotal - 0.01 && maxPriorTotal > 0);

  const datasets: import('chart.js').ChartDataset<'bar'>[] = [];
  if (showSl) {
    datasets.push({
      label: 'Sick Leave',
      data: current.map((p) => p.sickLeaveDays),
      backgroundColor: isPeak.map((peak) => (peak ? SICK_LEAVE_COLOR : MUTED_DARK)),
      borderRadius: 4,
      stack: 'current',
    });
  }
  if (showCsl) {
    datasets.push({
      label: 'Child Sick Leave',
      data: current.map((p) => p.childCareDays),
      backgroundColor: isPeak.map((peak) => (peak ? CHILD_CARE_COLOR : MUTED_LIGHT)),
      borderRadius: 4,
      stack: 'current',
    });
  }
  if (showPrior) {
    if (showSl) {
      datasets.push({
        label: 'Sick Leave (last year)',
        // 0 only when the prior day genuinely doesn't exist — a suppressed
        // day keeps its null rather than being coerced into a false "0".
        data: current.map((p) => {
          const priorDay = priorByDay.get(p.dayName);
          return priorDay ? priorDay.sickLeaveDays : 0;
        }),
        backgroundColor: isPriorPeak.map((peak) => (peak ? CHILD_CARE_COLOR : MUTED_DARK)),
        borderRadius: 4,
        stack: 'prior',
      });
    }
    if (showCsl) {
      datasets.push({
        label: 'Child Sick Leave (last year)',
        data: current.map((p) => {
          const priorDay = priorByDay.get(p.dayName);
          return priorDay ? priorDay.childCareDays : 0;
        }),
        backgroundColor: isPriorPeak.map((peak) => (peak ? CHILD_CARE_COLOR : MUTED_LIGHT)),
        borderRadius: 4,
        stack: 'prior',
      });
    }
  }

  return (
    <section className="rounded-xl border border-zinc-200 bg-white p-6 shadow-sm">
      <div className="flex items-baseline justify-between">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-500">
            Day-of-Week Pattern
            <InfoTooltip text="Total absence days by weekday (Monday–Friday), showing which days absences most often fall on. A raw day count, not a rate — there's no per-weekday working-days denominator to normalize it into a percentage." />
          </h2>
          <p className="mt-1 text-xs text-zinc-400">Total absence days by weekday (Monday–Friday)</p>
        </div>
        <TogglePill active={showPrior} onClick={() => setShowPrior((v) => !v)}>
          Compare previous year
        </TogglePill>
      </div>
      <div className="mt-4" style={{ height: 220 }}>
        <Bar
          data={{ labels: current.map((p) => p.dayName), datasets }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom',
                labels: { boxWidth: 10, filter: (item) => !item.text.includes('(last year)') },
              },
            },
            scales: {
              x: { stacked: true },
              y: { stacked: true, beginAtZero: true },
            },
          }}
        />
      </div>
    </section>
  );
}
